import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { ManageBaseResourcesModal } from '../modals/ManageBaseResourcesModal';
import { ChooseBaseModal } from '../modals/ChooseBaseModal';
import { SectionHeading } from '../shared/SectionHeading';
import { createIceSurface, createSubtleIceSurface } from '../../theme/surfaces';
import { BaseResourceCard } from '../shared/BaseResourceCard';
import { BaseResourceMiniCard } from '../shared/BaseResourceMiniCard';
import { miniFlexSx } from '../../App';

interface ManageWarbandBaseTabProps {
  resourcesModalOpen: boolean;
  onResourcesModalClose: () => void;
}

export const ManageWarbandBaseTab: React.FC<ManageWarbandBaseTabProps> = ({
  resourcesModalOpen,
  onResourcesModalClose,
}) => {
  const warbands = useWarbandStore((s) => s.warbands);
  const selectedWarbandId = useWarbandStore((s) => s.selectedWarbandId);
  const baseResources = useWarbandStore((s) => s.baseResources);
  const updateBaseResources = useDirtyTrackingStore((s) => s.updateBaseResources);

  const currentWarband = warbands.find((w) => w.id === selectedWarbandId);
  const currentBaseId = currentWarband?.baseId ?? null;
  const selectedBaseType = useReferenceDataStore((s) => s.getBaseTypeById(currentBaseId));
  const isMinified = useWarbandStore((s) => s.isMinified);
  const [baseMenuAnchor, setBaseMenuAnchor] = useState<null | HTMLElement>(null);
  const [chooseBaseOpen, setChooseBaseOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={createIceSurface(200, { icicles: true, card: true })} p={1.5}>
      <SectionHeading
        label="Base"
        action={
          <IconButton size="small" onClick={(e) => setBaseMenuAnchor(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        }
      />
      {/* Base Type Selection */}
      <Box sx={{ ...createSubtleIceSurface(500, {}), p: 2, borderRadius: 1 }}>
        {selectedBaseType ? (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Your base is: {selectedBaseType.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedBaseType.description}
            </Typography>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              onClick={() => setChooseBaseOpen(true)}
              disabled={!selectedWarbandId}
              fullWidth
            >
              Choose base
            </Button>
            <>
              <Typography variant="body2" color="text.secondary">
                Select a base for your warband. Each base provides a unique benefit between games.
              </Typography>
            </>
          </>
        )}
      </Box>

      {/* Base Resources */}
      {
        baseResources.length === 0 ? (
          <Box sx={{ ...createSubtleIceSurface(500, {}), p: 2, borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              No base resources purchased yet. Add resources to improve your base.
            </Typography>
          </Box>
        ) : isMinified ? (
          <Box sx={miniFlexSx}>
            {baseResources
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((resource) => (
                <BaseResourceMiniCard
                  key={resource.id}
                  resource={resource}
                  onRemove={(refundCost) => {
                    const remainingIds = baseResources.filter((r) => r.id !== resource.id).map((r) => r.id);
                    updateBaseResources(remainingIds, false, refundCost);
                  }}
                />
              ))}
          </Box>
        ) : (
          <Box>
            <Grid container spacing={1.5}>
              {baseResources
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((resource) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={resource.id}>
                    <BaseResourceCard
                      resource={resource}
                      onRemove={(refundCost) => {
                        const remainingIds = baseResources.filter((r) => r.id !== resource.id).map((r) => r.id);
                        updateBaseResources(remainingIds, false, refundCost);
                      }}
                    />
                  </Grid>
                ))}
            </Grid>
          </Box>
        )
      }

      <Menu
        anchorEl={baseMenuAnchor}
        open={Boolean(baseMenuAnchor)}
        onClose={() => setBaseMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setBaseMenuAnchor(null); setChooseBaseOpen(true); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change base</ListItemText>
        </MenuItem>
      </Menu>

      <ManageBaseResourcesModal
        open={resourcesModalOpen}
        onClose={onResourcesModalClose}
      />

      <ChooseBaseModal
        open={chooseBaseOpen}
        onClose={() => setChooseBaseOpen(false)}
        currentBaseId={currentBaseId}
      />
      </Box>

    </Box>
  );
};
